import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import PreguntaEditorInline from './components/PreguntaEditorInline';
import { usePreguntasOperations } from './hooks/usePreguntasOperations';
import ConfirmacionCancelarModal from '../../UI/ConfirmacionCancelarModal';
import './Step3Preguntas.css';

const Step3Preguntas = ({
  formData,
  setFormData,
  errors,
  clearError = () => {},
  isEditMode = false,
  evaluacionId = null,
  onChangesPending = () => {},
  isPsicologicaSalamanca = false
}) => {
  const [preguntaSeleccionada, setPreguntaSeleccionada] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [guardandoPregunta, setGuardandoPregunta] = useState(false);
  const [guardandoOpcion, setGuardandoOpcion] = useState(null);
  const [showEliminarPreguntaModal, setShowEliminarPreguntaModal] = useState(false);
  const [preguntaPendienteEliminar, setPreguntaPendienteEliminar] = useState(null);

  const { 
    guardarPreguntaEnBD, 
    guardarOpcionEnBD, 
    eliminarPreguntaDeBD,
    eliminarOpcionDeBD,
    guardarTodoEnBD
  } = usePreguntasOperations(evaluacionId);

  const OPCIONES_PSICOLOGICA_FIJAS = [
    { texto: 'Nunca', puntaje: 0 },
    { texto: 'A Veces', puntaje: 1 },
    { texto: 'Con frecuencia', puntaje: 2 },
    { texto: 'Siempre', puntaje: 3 }
  ];

  /**
   * Detecta si hay preguntas u opciones sin guardar en la base de datos
   * Preguntas sin guardar tienen ID que empieza con "nueva_"
   * Opciones sin guardar tienen ID que empieza con "opcion_"
   */
  const tieneElementosSinGuardar = () => {
    return formData.preguntas.some(pregunta => {
      // Verificar si la pregunta está sin guardar
      const preguntaSinGuardar = typeof pregunta.id === 'string' && pregunta.id.startsWith('nueva_');
      
      // Verificar si alguna opción está sin guardar
      const tieneOpcionesSinGuardar = pregunta.opciones?.some(opcion => 
        typeof opcion.id === 'string' && opcion.id.startsWith('opcion_')
      ) || false;

      return preguntaSinGuardar || tieneOpcionesSinGuardar;
    });
  };

  /**
   * Efecto para manejar la advertencia de salida cuando hay cambios sin guardar
   */
  useEffect(() => {
    const hayCambios = tieneElementosSinGuardar();
    
    // Notificar al componente padre sobre cambios pendientes
    if (typeof onChangesPending === 'function') {
      onChangesPending(isEditMode ? hayCambios : false);
    }

    const handleBeforeUnload = (e) => {
      if (hayCambios) {
        const message = 'Hay preguntas sin guardar. Si sale ahora, perderá los cambios.';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData.preguntas, onChangesPending, isEditMode]);

  const esPreguntaPendienteDeGuardado = (pregunta) => {
    if (!pregunta) return false;
    const preguntaSinGuardar = typeof pregunta.id === 'string' && pregunta.id.startsWith('nueva_');
    const opcionesSinGuardar = (pregunta.opciones || []).some(opcion =>
      typeof opcion.id === 'string' && opcion.id.startsWith('opcion_')
    );
    return preguntaSinGuardar || opcionesSinGuardar;
  };

  const limpiarTextoHtml = (valor = '') =>
    valor
      .toString()
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim();

  const esPreguntaNuevaVacia = (pregunta) => {
    if (!pregunta) return false;
    const esNueva = typeof pregunta.id === 'string' && pregunta.id.startsWith('nueva_');
    if (!esNueva) return false;

    const textoPreguntaVacio = !limpiarTextoHtml(pregunta.pregunta);
    const opcionesSinTexto = (pregunta.opciones || []).every(op => !limpiarTextoHtml(op.texto));

    return textoPreguntaVacio && opcionesSinTexto;
  };

  const agregarPregunta = () => {
    if (!isEditMode) {
      const ultimaPregunta = formData.preguntas?.[formData.preguntas.length - 1];
      if (ultimaPregunta && !limpiarTextoHtml(ultimaPregunta.pregunta)) {
        toast.warning('Complete el enunciado de la pregunta anterior antes de crear una nueva.');
        return;
      }
    }

    if (isEditMode && tieneElementosSinGuardar()) {
      toast.warning('Debe guardar la pregunta actual antes de crear una nueva.');
      return;
    }

    const timestamp = Date.now();
    const opcionesPorDefecto = isPsicologicaSalamanca
      ? OPCIONES_PSICOLOGICA_FIJAS.map((opcion, idx) => ({
          id: `opcion_${timestamp}_${idx + 1}`,
          texto: opcion.texto,
          esCorrecta: true,
          puntaje: opcion.puntaje
        }))
      : [
          { id: `opcion_${timestamp}_1`, texto: '', esCorrecta: false, puntaje: 0 },
          { id: `opcion_${timestamp}_2`, texto: '', esCorrecta: false, puntaje: 0 }
        ];

    const nuevaPregunta = {
      id: `nueva_${timestamp}`,
      pregunta: '',
      numero: formData.preguntas.length + 1,
      esObligatoria: true,
      puntaje: 0,
      opciones: opcionesPorDefecto
    };

    setFormData(prev => ({
      ...prev,
      preguntas: [...prev.preguntas, nuevaPregunta]
    }));

    setPreguntaSeleccionada(nuevaPregunta);
    setModoEdicion(true);
  };

  const eliminarPreguntaDelEstado = async (pregunta) => {
    if (!pregunta) return;

    const preguntaId = pregunta.id;
    let eliminacionExitosa = true;

    if (pregunta && evaluacionId) {
      eliminacionExitosa = await eliminarPreguntaDeBD(pregunta);
      if (!eliminacionExitosa) {
        return false;
      }
    }

    setFormData(prev => {
      const preguntasFiltradas = prev.preguntas.filter(p => p.id !== preguntaId);
      const preguntasRenumeradas = preguntasFiltradas.map((pregunta, index) => ({
        ...pregunta,
        numero: index + 1
      }));

      return { ...prev, preguntas: preguntasRenumeradas };
    });

    if (preguntaSeleccionada?.id === preguntaId) {
      setPreguntaSeleccionada(null);
      setModoEdicion(false);
    }

    return true;
  };

  const solicitarEliminarPregunta = (preguntaId) => {
    const pregunta = formData.preguntas.find(p => p.id === preguntaId);
    if (!pregunta) return;

    setPreguntaPendienteEliminar(pregunta);
    setShowEliminarPreguntaModal(true);
  };

  const handleDescartarRapido = async (preguntaId) => {
    const pregunta = formData.preguntas.find(p => p.id === preguntaId);
    if (!pregunta) return;

    if (esPreguntaNuevaVacia(pregunta)) {
      await eliminarPreguntaDelEstado(pregunta);
      return;
    }

    setPreguntaPendienteEliminar(pregunta);
    setShowEliminarPreguntaModal(true);
  };

  const eliminarPreguntaConfirmada = async () => {
    if (!preguntaPendienteEliminar) return;

    const eliminada = await eliminarPreguntaDelEstado(preguntaPendienteEliminar);
    if (eliminada) {
      toast.success('Se eliminó la pregunta exitosamente');
    }

    setPreguntaPendienteEliminar(null);
    setShowEliminarPreguntaModal(false);
  };

  const actualizarPregunta = (preguntaId, cambios) => {
    if (preguntaSeleccionada?.id === preguntaId) {
      setPreguntaSeleccionada(prevSelected => ({ ...prevSelected, ...cambios }));
    }
    setFormData(prev => ({
      ...prev,
      preguntas: prev.preguntas.map(pregunta =>
        pregunta.id === preguntaId ? { ...pregunta, ...cambios } : pregunta
      )
    }));
  };

  /**
   * Agregar nueva opción a una pregunta específica
   */
  const agregarOpcion = (preguntaId) => {
    if (isPsicologicaSalamanca) {
      toast.info('Las pruebas psicológicas Salamanca y Personalidad no permiten añadir más opciones.', {
        autoClose: 5000
      });
      return;
    }

    const pregunta = formData.preguntas.find(p => p.id === preguntaId);
    if (!pregunta) return;

    const nuevaOpcion = {
      id: `opcion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      texto: '',
      esCorrecta: false,
      puntaje: 0
    };

    const opcionesActualizadas = [...(pregunta.opciones || []), nuevaOpcion];
    actualizarPregunta(preguntaId, { opciones: opcionesActualizadas });
  };

  /**
   * Eliminar opción de la pregunta seleccionada
   */
  const eliminarOpcion = async (opcionId) => {
    if (!preguntaSeleccionada) return;

    if (isPsicologicaSalamanca) {
      toast.warning('En Salamanca y Personalidad no se pueden eliminar opciones.');
      return;
    }

    if (preguntaSeleccionada.opciones.length <= 2) {
      console.warn('Una pregunta debe tener al menos 2 opciones.');
      return;
    }

    // Si la opción ya existe en la BD (tiene un ID numérico), la eliminamos
    if (typeof opcionId === 'number' || (typeof opcionId === 'string' && !opcionId.startsWith('opcion_'))) {
      await eliminarOpcionDeBD(opcionId, preguntaSeleccionada.id, {
        onSuccess: () => {
          const opcionesFiltradas = preguntaSeleccionada.opciones.filter(o => o.id !== opcionId);
          actualizarPregunta(preguntaSeleccionada.id, { opciones: opcionesFiltradas });
        },
        onError: () => {
          alert('Hubo un error al eliminar la opción de la base de datos.');
        }
      });
    } else {
      // Si es una opción local (nueva), solo la quitamos del estado
      const opcionesFiltradas = preguntaSeleccionada.opciones.filter(o => o.id !== opcionId);
      actualizarPregunta(preguntaSeleccionada.id, { opciones: opcionesFiltradas });
    }
  };

  const actualizarOpcion = (opcionId, cambios) => {
    if (!preguntaSeleccionada) return;

    if (isPsicologicaSalamanca) {
      cambios = { ...cambios, esCorrecta: true };
    }

    if (cambios.puntaje !== undefined) {
      const puntajeNumerico = parseFloat(cambios.puntaje) || 0;
      if (!isPsicologicaSalamanca && puntajeNumerico > 0 && !cambios.hasOwnProperty('esCorrecta')) {
        cambios = { ...cambios, esCorrecta: true };
      }
    }

    const opcionesActualizadas = preguntaSeleccionada.opciones.map(opcion =>
      opcion.id === opcionId ? { ...opcion, ...cambios } : opcion
    );

    actualizarPregunta(preguntaSeleccionada.id, { opciones: opcionesActualizadas });
  };


  const handleGuardarPregunta = async () => {
    if (!preguntaSeleccionada) return;

    await guardarPreguntaEnBD(preguntaSeleccionada, {
      onStart: () => setGuardandoPregunta(true),
      onSuccess: (idReal) => {
        if (idReal) {
          actualizarPregunta(preguntaSeleccionada.id, { id: idReal });
        }
        setGuardandoPregunta(false);
      },
      onError: () => setGuardandoPregunta(false)
    });
  };

  const validarPreguntaAntesDeSalir = () => {
    if (!preguntaSeleccionada) return true;

    // Validación: Pregunta no vacía
    const textoPreguntaLimpio = (preguntaSeleccionada.pregunta || '').replace(/<[^>]*>/g, '').trim();
    if (!textoPreguntaLimpio) {
      toast.error('El texto de la pregunta no puede estar vacío. Complételo o elimine la pregunta.');
      return false;
    }

    // Validación: Opciones no vacías
    const opcionesVacias = (preguntaSeleccionada.opciones || []).filter(op => {
      const textoOpcionLimpio = (op.texto || '').replace(/<[^>]*>/g, '').trim();
      return !textoOpcionLimpio;
    });

    if (opcionesVacias.length > 0) {
      toast.error('Existen opciones vacías. Complételas o elimínelas antes de salir.');
      return false;
    }

    return true;
  };

  const handleCancelEdit = () => {
    if (validarPreguntaAntesDeSalir()) {
      setModoEdicion(false);
      setPreguntaSeleccionada(null);
    }
  };

  const handleGuardarTodo = async () => {
    if (!preguntaSeleccionada) return;

    if (!validarPreguntaAntesDeSalir()) return;

    if (!evaluacionId) {
      setModoEdicion(false);
      setPreguntaSeleccionada(null);
      return;
    }
    setGuardandoPregunta(true);

    await guardarTodoEnBD(preguntaSeleccionada, {
      onStart: () => console.log('Iniciando guardado completo en BD...'),
      onProgress: (msg) => console.log(msg),
      onSuccess: (idReal, opcionesActualizadas) => {
        console.log('[OK] Guardado completo exitoso');
        console.log('   - ID real pregunta:', idReal);
        console.log('   - Opciones actualizadas:', opcionesActualizadas);
        
        // Actualizar pregunta con ID real y opciones con IDs reales
        const cambios = {
          ...(idReal && { id: idReal }),
          ...(opcionesActualizadas && { opciones: opcionesActualizadas })
        };
        
        if (Object.keys(cambios).length > 0) {
          actualizarPregunta(preguntaSeleccionada.id, cambios);
        }
        
        setGuardandoPregunta(false);
        setGuardandoOpcion(null);
        
        // Cerrar la edición automáticamente
        setModoEdicion(false);
        setPreguntaSeleccionada(null);
      },
      onError: () => {
        setGuardandoPregunta(false);
        setGuardandoOpcion(null);
      }
    });
  };

  useEffect(() => {
    const puntajeTotal = formData.preguntas.reduce((total, pregunta) => {
      const puntajePregunta = pregunta.opciones?.reduce((suma, opcion) => 
        suma + (parseFloat(opcion.puntaje) || 0), 0) || 0;
      return total + puntajePregunta;
    }, 0);
    
    if (formData.puntajeTotal !== puntajeTotal) {
      setFormData(prev => ({ ...prev, puntajeTotal }));
    }
  }, [formData.preguntas, formData.puntajeTotal, setFormData]);

  // Notificar al padre cuando hay cambios pendientes
  useEffect(() => {
    if (evaluacionId) {
      const hasPending = tieneElementosSinGuardar();
      onChangesPending(hasPending);
    }
  }, [formData.preguntas, evaluacionId, onChangesPending]);

  return (
    <div className="step3-container">
      <div className="step3-card">
        <div className="step3-section-header">
          <div className="step3-header-content">
            <h2 className="step3-section-title">
              <span className="step3-section-icon">❓</span>
              Preguntas de la Evaluación
            </h2>
            <p className="step3-section-subtitle">
              Cree y configure las preguntas que formarán parte de su evaluación
            </p>
          </div>
        </div>

        {errors.preguntas && (
          <div className="step3-error-alert">
            <span className="step3-error-icon">⚠️</span>
            <span>{errors.preguntas}</span>
          </div>
        )}

        {formData.preguntas.length === 0 ? (
          <div className="step3-empty-state">
            <div className="step3-empty-icon">❓</div>
            <h3 className="step3-empty-title">No hay preguntas aún</h3>
            <p className="step3-empty-text">
              Agregue preguntas para que los usuarios puedan responder en esta evaluación
            </p>
            <button
              type="button"
              className="step3-btn-empty"
              onClick={agregarPregunta}
            >
              <span className="step3-btn-icon">+</span>
              Crear Primera Pregunta
            </button>
          </div>
        ) : (
          <>
            <div className="step3-preguntas-list">
              {formData.preguntas.map((pregunta) => (
                <PreguntaEditorInline
                  key={pregunta.id}
                  pregunta={pregunta}
                  hasPendingSave={esPreguntaPendienteDeGuardado(pregunta)}
                  isPsicologicaSalamanca={isPsicologicaSalamanca}
                  isEditing={preguntaSeleccionada?.id === pregunta.id && modoEdicion}
                  onStartEdit={(p) => {
                    setPreguntaSeleccionada(p);
                    setModoEdicion(true);
                  }}
                  onCancelEdit={handleCancelEdit}
                  onUpdatePregunta={(cambios) => actualizarPregunta(pregunta.id, cambios)}
                  onUpdateOpcion={actualizarOpcion}
                  onAgregarOpcion={() => agregarOpcion(pregunta.id)}
                  onEliminarOpcion={eliminarOpcion}
                  onEliminar={solicitarEliminarPregunta}
                  onDescartarRapido={() => handleDescartarRapido(pregunta.id)}
                  onGuardarTodo={handleGuardarTodo}
                  guardandoPregunta={guardandoPregunta}
                  guardandoOpcion={guardandoOpcion}
                  evaluacionId={evaluacionId}
                  isEditMode={isEditMode}
                />
              ))}
            </div>

            <div className="step3-add-bottom-wrap">
              <button
                type="button"
                className="step3-btn-add"
                onClick={agregarPregunta}
                title="Agregar nueva pregunta"
              >
                <span className="step3-btn-icon">+</span>
                Agregar Pregunta
              </button>
            </div>
          </>
        )}

        <ConfirmacionCancelarModal
          show={showEliminarPreguntaModal}
          currentStep={1}
          totalSteps={4}
          title="Eliminar pregunta"
          icon="trash-alt"
          iconColor="#dc3545"
          message="¿Está seguro de eliminar esta pregunta? Esta acción no se puede deshacer."
          secondaryMessage={preguntaPendienteEliminar ? `Pregunta ${preguntaPendienteEliminar.numero}` : ''}
          confirmLabel="Sí, eliminar"
          cancelLabel="Cancelar"
          onConfirm={eliminarPreguntaConfirmada}
          onCancel={() => {
            setShowEliminarPreguntaModal(false);
            setPreguntaPendienteEliminar(null);
          }}
        />
      </div>
    </div>
  );
};

export default Step3Preguntas;
